import { ComponentProps } from "react";
import { Ionicons } from "@expo/vector-icons";

export type EntityType = "memory" | "media" | "contact" | "group" | "share" | "message" | "place" | "anchor";

export interface IEntityIconConfig {
  /** Canonical entity type */
  type: EntityType;
  /** Primary filled Ionicons glyph */
  ionicon: ComponentProps<typeof Ionicons>["name"];
  /** Outline variant Ionicons glyph */
  ioniconOutline: ComponentProps<typeof Ionicons>["name"];
  /** Apple SF Symbol name */
  sfSymbol: string;
  /** Google Material Icon name */
  materialIcon: string;
  /** Singular user-facing label */
  label: string;
  /** Plural user-facing label */
  pluralLabel: string;
  /** Descriptive summary */
  description: string;
}

/**
 * Standardized entity icon and metadata definitions.
 * Central reference for Memory, Media, Contact, Group, and Share iconography across web and mobile.
 */
export const ENTITY_ICONS: Record<EntityType, IEntityIconConfig> = {
  memory: {
    type: "memory",
    ionicon: "book",
    ioniconOutline: "book-outline",
    sfSymbol: "book.closed.fill",
    materialIcon: "auto-stories",
    label: "Memory",
    pluralLabel: "Memories",
    description: "Personal keepsakes and narrative life moments",
  },
  media: {
    type: "media",
    ionicon: "images",
    ioniconOutline: "images-outline",
    sfSymbol: "photo.stack.fill",
    materialIcon: "perm-media",
    label: "Media",
    pluralLabel: "Media",
    description: "Photos, videos, and audio recordings",
  },
  contact: {
    type: "contact",
    ionicon: "person",
    ioniconOutline: "person-outline",
    sfSymbol: "person.fill",
    materialIcon: "person",
    label: "Contact",
    pluralLabel: "Contacts",
    description: "Trusted connections and individuals",
  },
  group: {
    type: "group",
    ionicon: "people",
    ioniconOutline: "people-outline",
    sfSymbol: "person.3.fill",
    materialIcon: "groups",
    label: "Group",
    pluralLabel: "Groups",
    description: "Circles of family, friends, and collaborators",
  },
  share: {
    type: "share",
    ionicon: "share-social",
    ioniconOutline: "share-social-outline",
    sfSymbol: "share.fill",
    materialIcon: "share",
    label: "Share",
    pluralLabel: "Shares",
    description: "Access sharing and permissions",
  },
  message: {
    type: "message",
    ionicon: "mail",
    ioniconOutline: "mail-outline",
    sfSymbol: "envelope.fill",
    materialIcon: "mail",
    label: "Message",
    pluralLabel: "Messages",
    description: "Direct notifications and collaborative messages",
  },
  place: {
    type: "place",
    ionicon: "location",
    ioniconOutline: "location-outline",
    sfSymbol: "location.fill",
    materialIcon: "place",
    label: "Place",
    pluralLabel: "Places",
    description: "Physical venues, geographical landmarks, and destinations",
  },
  anchor: {
    type: "anchor",
    ionicon: "boat",
    ioniconOutline: "boat-outline",
    sfSymbol: "anchor",
    materialIcon: "anchor",
    label: "Anchor",
    pluralLabel: "Anchors",
    description: "Personal anchor references, animals, hobbies, and courses",
  },
};

/**
 * Normalizes loose type strings (e.g. 'image', 'images', 'photos', 'contacts', 'users')
 * into a canonical EntityType.
 *
 * @param {string} [type] - Raw type string.
 * @returns {EntityType} Normalized entity type.
 */
export function normalizeEntityType(type?: string): EntityType {
  const t = (type || "").toLowerCase().trim();
  if (t === "memory" || t === "memories") return "memory";
  if (
    t === "media" ||
    t === "image" ||
    t === "images" ||
    t === "video" ||
    t === "videos" ||
    t === "audio" ||
    t === "photo" ||
    t === "photos"
  ) {
    return "media";
  }
  if (t === "contact" || t === "contacts" || t === "user" || t === "users") return "contact";
  if (t === "group" || t === "groups") return "group";
  if (t === "share" || t === "shares") return "share";
  if (t === "message" || t === "messages") return "message";
  if (t === "place" || t === "places" || t === "location" || t === "locations") return "place";
  if (t === "anchor" || t === "anchors") return "anchor";
  return "memory";
}

/**
 * Retrieves the icon configuration for a given entity type.
 *
 * @param {string} [type] - Entity type identifier.
 * @returns {IEntityIconConfig} The resolved entity icon configuration.
 */
export function getEntityIconConfig(type?: string): IEntityIconConfig {
  return ENTITY_ICONS[normalizeEntityType(type)];
}

/**
 * Retrieves human-readable label for an entity type.
 *
 * @param {string} [type] - Entity type identifier.
 * @param {boolean} [plural=false] - Whether to return plural form.
 * @returns {string} Entity label.
 */
export function getEntityLabel(type?: string, plural = false): string {
  const config = getEntityIconConfig(type);
  return plural ? config.pluralLabel : config.label;
}
